import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { MapPin, Phone, Clock, ChevronLeft, Hospital } from 'lucide-react';
import api from '../lib/api';

export default function ClinicDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [clinic, setClinic] = useState(location.state || null);
  const [loading, setLoading] = useState(!location.state);

  useEffect(() => {
    if (clinic) return;
    if (!id) return;

    let cancelled = false;
    setLoading(true);

    api.get('/api/nearby-clinics')
      .then((res) => {
        const list = Array.isArray(res?.data?.clinics) ? res.data.clinics : [];
        const found = list.find((c) => String(c._id) === String(id));
        if (!cancelled && found) setClinic(found);
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id, clinic]);

  if (loading) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: 24, textAlign: 'center' }}>
        Loading clinic details...
      </div>
    );
  }

  if (!clinic) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: 24, textAlign: 'center' }}>
        <p>Clinic not found.</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', minHeight: '100vh', background: 'var(--BG)', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e5e5' }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 10, display: 'flex', alignItems: 'center', color: 'var(--primary)' }}
        >
          <ChevronLeft size={22} />
        </button>
        <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>Clinic Details</h5>
      </div>

      {/* Hero image */}
      <div style={{ width: '100%', height: 220, overflow: 'hidden', background: 'rgba(106,58,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {clinic.image ? (
          <img src={clinic.image} alt={clinic.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Hospital size={64} color="var(--primary)" />
        )}
      </div>

      {/* Core info */}
      <div style={{ padding: '20px 16px 0' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>{clinic.name}</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>{clinic.area}</p>

        {/* Detail rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {clinic.address ? (
            <DetailRow icon={<MapPin size={18} color="var(--primary)" />} label="Address" value={clinic.address} />
          ) : null}

          {clinic.phone ? (
            <DetailRow
              icon={<Phone size={18} color="var(--primary)" />}
              label="Phone"
              value={
                <a href={`tel:${clinic.phone}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>
                  {clinic.phone}
                </a>
              }
            />
          ) : null}

          {clinic.eta ? (
            <DetailRow icon={<Clock size={18} color="var(--primary)" />} label="ETA" value={clinic.eta} />
          ) : null}
        </div>

        {/* Call CTA */}
        {clinic.phone ? (
          <a
            href={`tel:${clinic.phone}`}
            style={{
              display: 'block',
              marginTop: 28,
              padding: '14px 0',
              borderRadius: 14,
              background: 'var(--primary)',
              color: '#fff',
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 15,
              textDecoration: 'none',
            }}
          >
            Call Clinic
          </a>
        ) : null}
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 11, color: '#999', letterSpacing: '0.2px', marginBottom: 2, fontWeight: 500 }}>
          {label}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{value}</div>
      </div>
    </div>
  );
}
